/**
 * SEO Autopilot dashboard — the agentic SEO control surface.
 *
 * Suggestion-first model: the autopilot researches keywords/trends and DRAFTS
 * changes; the admin approves or rejects here. Nothing reaches the live site
 * without approval. Sections:
 *   1. Enable + cadence (Pro+).
 *   2. Pending proposals — approve/reject (the core of suggestion-first).
 *   3. Active target keywords (what's currently driving llms.txt + the blog).
 *   4. Keyword explorer — the researched universe (view-only).
 *   5. Activity log — what the autopilot proposed / you approved.
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  CheckCircle2,
  XCircle,
  Crown,
  Clock,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiHelpers } from '@/lib/api';

interface KeywordInsight {
  term: string;
  source: string;
  volume?: number;
  cpc?: number;
  intent?: 'informational' | 'commercial' | 'transactional' | 'navigational';
  score: number;
  /** From the quality gate (Phase 1). */
  relevance?: number;
  suggestedAction?: 'optimize-page' | 'write-blog' | 'add-faq' | 'track-only';
  why?: string;
}

const ACTION_LABEL: Record<string, string> = {
  'optimize-page': 'Optimize page',
  'write-blog': 'Write blog',
  'add-faq': 'Add FAQ',
  'track-only': 'Track',
};
/** Per-plan active-keyword quota (mirror of the backend). */
const KEYWORD_QUOTA: Record<string, number> = { pro: 20, business: 50 };
interface Proposal {
  id: string;
  at: string;
  summary: string;
  payload: { targetKeywords?: string[]; targetAudience?: string; blogTopics?: string[] };
}
interface ActionRow {
  at: string;
  type: string;
  detail: string;
  status: 'applied' | 'suggested';
}
interface AgenticState {
  agentic: { enabled?: boolean; cadenceDays?: number; lastRunAt?: string; autoApply?: boolean };
  keywords: KeywordInsight[];
  targetKeywords: string[];
  keywordsUpdatedAt: string | null;
  blogBacklog: Array<{ keyword: string; addedAt: string; usedAt?: string }>;
  pendingProposals: Proposal[];
  recentActions: ActionRow[];
}

const INTENT_COLOR: Record<string, string> = {
  transactional: 'bg-green-100 text-green-700',
  commercial: 'bg-blue-100 text-blue-700',
  informational: 'bg-amber-100 text-amber-700',
  navigational: 'bg-gray-100 text-gray-600',
};

export default function SeoAutopilotDashboard() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cadence, setCadence] = useState(7);

  // Plan (for gating the toggle to Pro+).
  const { data: manifestData } = useQuery({
    queryKey: ['plugin-manifest', projectId, 'seo-copilot'],
    queryFn: () => apiHelpers.getPluginManifest(projectId!, 'seo-copilot'),
    enabled: Boolean(projectId),
  });
  const plan: string = (manifestData?.data?.data?.installed?.config?.plan as string) || 'free';
  const isPro = plan === 'pro' || plan === 'business';
  const isBusiness = plan === 'business';
  const keywordQuota = KEYWORD_QUOTA[plan] ?? 10;

  // Autopilot state.
  const { data, isLoading } = useQuery({
    queryKey: ['seo-autopilot', projectId],
    queryFn: () => apiHelpers.seoCopilotGetAgentic(projectId!),
    enabled: Boolean(projectId),
  });
  const state: AgenticState | undefined = data?.data?.data;
  const enabled = Boolean(state?.agentic?.enabled);
  const autoApply = Boolean(state?.agentic?.autoApply);
  const effectiveCadence = state?.agentic?.cadenceDays ?? cadence;

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['seo-autopilot', projectId] });

  const toggleMutation = useMutation({
    mutationFn: (next: boolean) =>
      apiHelpers.seoCopilotSetAgentic(projectId!, {
        enabled: next,
        cadenceDays: effectiveCadence,
        autoApply,
      }),
    onSuccess: (_res, next) => {
      invalidate();
      toast.success(next ? 'Autopilot enabled' : 'Autopilot paused');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Could not update Autopilot'),
  });

  const autoApplyMutation = useMutation({
    mutationFn: (next: boolean) =>
      apiHelpers.seoCopilotSetAgentic(projectId!, {
        enabled,
        cadenceDays: effectiveCadence,
        autoApply: next,
      }),
    onSuccess: (_res, next) => {
      invalidate();
      toast.success(next ? 'Auto-apply on — changes go live automatically' : 'Auto-apply off');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Could not update auto-apply'),
  });

  const approveMutation = useMutation({
    mutationFn: (proposalId: string) =>
      apiHelpers.seoCopilotApproveProposal(projectId!, proposalId),
    onSuccess: () => {
      invalidate();
      toast.success('Approved — changes are now live');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message || 'Approve failed'),
  });

  const rejectMutation = useMutation({
    mutationFn: (proposalId: string) => apiHelpers.seoCopilotRejectProposal(projectId!, proposalId),
    onSuccess: () => {
      invalidate();
      toast.success('Proposal dismissed');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message || 'Reject failed'),
  });

  const runNowMutation = useMutation({
    mutationFn: () => apiHelpers.seoCopilotRunAgentic(projectId!),
    onSuccess: (res: any) => {
      invalidate();
      toast.success(res?.data?.data?.message || 'Autopilot ran');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message || 'Run failed'),
  });

  if (!projectId) return null;

  const proposals = state?.pendingProposals || [];
  const keywords = state?.keywords || [];
  const targetKeywords = state?.targetKeywords || [];
  const actions = state?.recentActions || [];

  return (
    <div className="space-y-6 p-6">
      <button
        onClick={() => navigate(`/projects/${projectId}/plugins/seo-copilot/dashboard`)}
        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft className="h-4 w-4" /> Back to SEO Dashboard
      </button>

      <div className="flex items-center gap-2">
        <Sparkles className="h-6 w-6 text-violet-500" />
        <h1 className="text-2xl font-semibold">SEO Autopilot</h1>
      </div>
      <p className="text-muted-foreground -mt-3 text-sm">
        Automatic keyword &amp; trend research, grounded in real Search Console data and vetted by a
        quality gate. By default it drafts changes for your approval; Business can switch on
        hands-off auto-apply. It only touches your llms.txt, schema, and blog — never your source
        code.
      </p>

      {/* ── 1. Enable + cadence ─────────────────────────────────────── */}
      <section className="bg-card rounded-xl border p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-lg font-medium">
              Autopilot
              {!isPro && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                  <Crown className="h-3 w-3" /> Requires Pro
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              {enabled
                ? 'On — researching on a schedule and queuing proposals for you.'
                : 'Off — turn on to let the agent research and propose improvements.'}
            </p>
            {state?.agentic?.lastRunAt && (
              <p className="text-muted-foreground mt-1 inline-flex items-center gap-1 text-xs">
                <Clock className="h-3 w-3" /> Last run{' '}
                {new Date(state.agentic.lastRunAt).toLocaleString()}
              </p>
            )}
          </div>
          <button
            onClick={() => toggleMutation.mutate(!enabled)}
            disabled={!isPro || toggleMutation.isPending}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition disabled:opacity-50 ${
              enabled ? 'bg-violet-600' : 'bg-gray-300'
            }`}
            aria-label="Toggle Autopilot"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                enabled ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <label className="text-muted-foreground text-sm">Run every</label>
            <select
              value={effectiveCadence}
              onChange={(e) => {
                const v = Number(e.target.value);
                setCadence(v);
                if (enabled) toggleMutation.mutate(true); // re-save cadence while on
              }}
              disabled={!isPro}
              className="rounded-lg border px-2 py-1 text-sm disabled:opacity-50"
            >
              {[1, 3, 7, 14, 30].map((d) => (
                <option key={d} value={d}>
                  {d} day{d > 1 ? 's' : ''}
                </option>
              ))}
            </select>
          </div>
          <span className="text-muted-foreground text-xs">
            Tracks up to <strong>{keywordQuota}</strong> keywords on your {plan} plan.
          </span>
          <button
            onClick={() => runNowMutation.mutate()}
            disabled={!isPro || !enabled || runNowMutation.isPending}
            className="bg-primary text-primary-foreground ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {runNowMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Research now
          </button>
        </div>

        {/* Auto-apply (Business) — the hands-off mode. */}
        <div className="mt-4 flex items-start justify-between gap-4 rounded-lg border border-violet-200 bg-violet-50/50 p-3 dark:border-violet-900/40 dark:bg-violet-900/10">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium">
              Apply changes automatically
              {!isBusiness && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                  <Crown className="h-3 w-3" /> Business
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-0.5 text-xs">
              Hands-off mode: the agent applies low-risk changes (keywords, schema, blog topics) on
              its own — no approval step. Never touches your source code. You can switch back to
              review-first anytime.
            </p>
          </div>
          <button
            onClick={() => autoApplyMutation.mutate(!autoApply)}
            disabled={!isBusiness || !enabled || autoApplyMutation.isPending}
            className={`relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition disabled:opacity-40 ${
              autoApply ? 'bg-violet-600' : 'bg-gray-300'
            }`}
            aria-label="Toggle auto-apply"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                autoApply ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </section>

      {/* ── 2. Pending proposals ────────────────────────────────────── */}
      <section className="bg-card rounded-xl border p-5">
        <h2 className="mb-1 text-lg font-medium">Pending proposals</h2>
        <p className="text-muted-foreground mb-4 text-sm">
          Review what the autopilot wants to change. Approving applies it to your llms.txt, schema,
          and auto-blog topic queue.
        </p>

        {isLoading && <Loader2 className="h-5 w-5 animate-spin text-violet-500" />}
        {!isLoading && proposals.length === 0 && (
          <p className="text-muted-foreground text-sm">
            No proposals right now.{' '}
            {enabled ? 'The next run will draft some.' : 'Turn on Autopilot to start.'}
          </p>
        )}

        <div className="space-y-4">
          {proposals.map((p) => (
            <div key={p.id} className="rounded-lg border p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-medium">{p.summary}</span>
                <span className="text-muted-foreground text-xs">
                  {new Date(p.at).toLocaleDateString()}
                </span>
              </div>

              {p.payload.targetKeywords && p.payload.targetKeywords.length > 0 && (
                <div className="mb-2">
                  <div className="text-muted-foreground mb-1 text-xs font-medium uppercase">
                    Focus keywords
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {p.payload.targetKeywords.map((k) => (
                      <span
                        key={k}
                        className="rounded-full bg-violet-50 px-2 py-0.5 text-xs text-violet-700"
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {p.payload.blogTopics && p.payload.blogTopics.length > 0 && (
                <div className="mb-2">
                  <div className="text-muted-foreground mb-1 text-xs font-medium uppercase">
                    Blog topics to queue
                  </div>
                  <ul className="list-disc pl-5 text-sm">
                    {p.payload.blogTopics.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}

              {p.payload.targetAudience && (
                <div className="mb-2">
                  <div className="text-muted-foreground mb-1 text-xs font-medium uppercase">
                    Target audience
                  </div>
                  <p className="text-sm italic">“{p.payload.targetAudience}”</p>
                </div>
              )}

              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => approveMutation.mutate(p.id)}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  {approveMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  )}
                  Approve &amp; apply
                </button>
                <button
                  onClick={() => rejectMutation.mutate(p.id)}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  <XCircle className="h-3.5 w-3.5" /> Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── 3. Active target keywords ───────────────────────────────── */}
      {targetKeywords.length > 0 && (
        <section className="bg-card rounded-xl border p-5">
          <h2 className="mb-1 text-lg font-medium">Active target keywords</h2>
          <p className="text-muted-foreground mb-3 text-sm">
            Currently driving your llms.txt topics and auto-blog focus.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {targetKeywords.map((k) => (
              <span
                key={k}
                className="rounded-full bg-violet-100 px-2.5 py-1 text-sm text-violet-800"
              >
                {k}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* ── 4. Keyword explorer ─────────────────────────────────────── */}
      <section className="bg-card rounded-xl border p-5">
        <div className="mb-3 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-violet-500" />
          <h2 className="text-lg font-medium">Keyword research</h2>
          {state?.keywordsUpdatedAt && (
            <span className="text-muted-foreground text-xs">
              updated {new Date(state.keywordsUpdatedAt).toLocaleDateString()}
            </span>
          )}
        </div>
        <p className="text-muted-foreground mb-3 text-xs">
          Vetted by the quality gate — junk and bare-brand fragments are filtered out. Terms you
          already rank for (from Search Console) are grounded in real impressions.
        </p>
        {keywords.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No research yet. {enabled ? 'It populates after the first run.' : 'Turn on Autopilot.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-left text-xs uppercase">
                <tr>
                  <th className="py-2 pr-2">Keyword</th>
                  <th className="py-2 pr-2">Intent</th>
                  <th className="py-2 pr-2">Planned action</th>
                  <th className="py-2 pr-2">Why</th>
                  <th className="py-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {keywords.slice(0, 40).map((k) => (
                  <tr key={k.term} className="border-t align-top">
                    <td className="py-1.5 pr-2 font-medium">{k.term}</td>
                    <td className="py-1.5 pr-2">
                      {k.intent && (
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${INTENT_COLOR[k.intent] || ''}`}
                        >
                          {k.intent}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-2">
                      {k.suggestedAction ? (
                        <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/20 dark:text-violet-300">
                          {ACTION_LABEL[k.suggestedAction] || k.suggestedAction}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="text-muted-foreground max-w-[240px] py-1.5 pr-2 text-xs">
                      {k.why || '—'}
                    </td>
                    <td className="py-1.5">
                      {k.source === 'gsc' ? (
                        <span
                          className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/20 dark:text-green-300"
                          title="You already rank for this in Search Console"
                        >
                          ranks now
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">{k.source}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── 5. Activity log ─────────────────────────────────────────── */}
      <section className="bg-card rounded-xl border p-5">
        <h2 className="mb-3 text-lg font-medium">Activity</h2>
        {actions.length === 0 ? (
          <p className="text-muted-foreground text-sm">Nothing yet.</p>
        ) : (
          <ul className="space-y-2">
            {actions.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span
                  className={`mt-0.5 rounded-full px-2 py-0.5 text-xs ${
                    a.status === 'applied'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-violet-100 text-violet-700'
                  }`}
                >
                  {a.status}
                </span>
                <span className="flex-1">{a.detail}</span>
                <span className="text-muted-foreground text-xs">
                  {new Date(a.at).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
